import React, {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { AlertTriangle, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Button } from "@/components/ui/button";
import {
  getPreciseLocationPermissionState,
  type PreciseLocationPermissionState,
  requestPreciseLocationAccess,
} from "@/shared/utils/location";
import { openLocationSettings } from "@/lib/locationSettings";
import { useEmployeeShiftApiSettings } from "../hooks";

export const GEOFENCING_GPS_MESSAGES = {
  start:
    "Please enable GPS to start your shift. If you choose not to, this will be marked on your shift.",
  end: "Please enable GPS to end your shift. If you choose not to, this will be marked on your shift.",
} as const;

export type GeofencingGpsNoticeVariant = keyof typeof GEOFENCING_GPS_MESSAGES;

function subscribeVisibility(onStoreChange: () => void) {
  if (typeof document === "undefined") {
    return () => {};
  }
  document.addEventListener("visibilitychange", onStoreChange);
  return () => document.removeEventListener("visibilitychange", onStoreChange);
}

function getVisibilitySnapshot(): DocumentVisibilityState | "hidden" {
  if (typeof document === "undefined") return "hidden";
  return document.visibilityState;
}

interface GeofencingGpsNoticeProps {
  variant: GeofencingGpsNoticeVariant;
}

/**
 * Non-blocking warning when org geofencing is enabled but precise location is not available.
 * Renders inline (e.g. inside Location Check card). Dismiss hides it for this visit only;
 * it shows again the next time the page (or this tree) mounts.
 */
export function GeofencingGpsNotice({ variant }: GeofencingGpsNoticeProps) {
  const settings = useEmployeeShiftApiSettings();
  const [permissionState, setPermissionState] =
    useState<PreciseLocationPermissionState>("unavailable");
  const [dismissed, setDismissed] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  const refresh = useCallback(async () => {
    const state = await getPreciseLocationPermissionState();
    setPermissionState(state);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibility = useSyncExternalStore(
    subscribeVisibility,
    getVisibilitySnapshot,
    () => "hidden" as const
  );

  useEffect(() => {
    if (visibility === "visible") {
      void refresh();
    }
  }, [visibility, refresh]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let listenerHandle: { remove: () => Promise<void> } | undefined;

    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void refresh();
    }).then((h) => {
      if (cancelled) {
        void h.remove();
        return;
      }
      listenerHandle = h;
    });

    return () => {
      cancelled = true;
      void listenerHandle?.remove();
    };
  }, [refresh]);

  const show =
    settings?.geofencing_enabled === true &&
    permissionState !== "granted" &&
    !dismissed;

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleRequestLocation = async () => {
    if (isNative) {
      await openLocationSettings();
      return;
    }
    await requestPreciseLocationAccess();
    await refresh();
  };

  const showActionButton = isNative || permissionState !== "denied";
  const actionLabel = isNative ? "Open settings" : "Enable GPS";

  if (!show) {
    return null;
  }

  return (
    <div
      className="relative rounded-lg border border-red-200 bg-red-50 p-3 pt-4"
      role="alert"
      data-testid="geofencing-gps-notice"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-2 top-2 rounded-md p-1 text-red-700 hover:bg-red-100 tap-highlight-transparent"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
      <div className="flex gap-3 items-start pr-8">
        <AlertTriangle
          className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5"
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-950 leading-snug">
            {GEOFENCING_GPS_MESSAGES[variant]}
          </p>
          {showActionButton ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 border-red-400 text-red-950 hover:bg-red-100"
              onClick={() => void handleRequestLocation()}
            >
              {actionLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
